import React, { useCallback, useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import audioStyles from 'components/answers/FileEmbed/audio.module.scss'
import {
    findMountedOwnerForMediaKey,
    getMediaOwnerPlaybackSnapshot,
    getMediaPlaybackState,
    seekMediaKey,
    subscribeToMediaDock,
    subscribeToMediaPlaybackState,
} from 'state/mediaPlaybackCoordinator'
import { incrementMediaDockQaCounter } from 'lib/mediaDockQaDiagnostics'
import { retainMediaDockProgress } from './mediaDockPollPolicy'
import styles from './media-dock.module.scss'

const CONTROLLER_PROGRESS_INTERVAL_MS = 250
const COLLAPSED_CONTROLLER_PROGRESS_INTERVAL_MS = 1000
const OWNER_CHANGE_EVENTS = new Set(['claim', 'release', 'ownerLost', 'ownerOffscreen'])

const formatDockTime = seconds => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00'

    const totalSeconds = Math.floor(seconds)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const remainingSeconds = totalSeconds % 60

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(
            2,
            '0',
        )}`
    }
    return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`
}

const buildDockProgressStyle = progressPercent =>
    /** @type {React.CSSProperties & { '--dock-progress': string }} */ ({
        '--dock-progress': `${progressPercent}%`,
    })

const stopEvent = event => event.stopPropagation()

const isNativeMediaOwner = owner => {
    const tagName = owner?.mediaEl?.tagName
    return tagName === 'AUDIO' || tagName === 'VIDEO'
}

const readStoredProgress = (mediaKey, previous = {}) => {
    const stored = getMediaPlaybackState(mediaKey)
    return {
        currentTime: Number.isFinite(stored?.currentTime) ? stored.currentTime : 0,
        duration:
            Number.isFinite(stored?.duration) && stored.duration > 0
                ? stored.duration
                : previous.duration,
    }
}

/**
 * The only dock subtree that follows playback time. Native media publishes
 * progress through browser events; controller-backed players such as YouTube
 * retain a small fallback poll because their iframe API has no timeupdate event.
 *
 * Variants: `seek` is the dock's own slider with times — the collapsed stack's
 * and cinema's — `stacked` the expanded tile's two rows (the slider on top,
 * "time / duration" and the `children` controls under it, as on the radio
 * card), and `hairline` the collapsed video's progress line.
 */
const MediaDockProgress = ({
    mediaKey,
    isPlaying,
    isDocumentVisible,
    isExpanded,
    variant = 'seek',
    liveRadio = false,
    onDraggingChange = undefined,
    children = null,
}) => {
    incrementMediaDockQaCounter('progress.renders')
    const [progress, setProgress] = useState({ currentTime: 0, duration: 0 })
    const isDraggingRef = useRef(false)

    const publishProgress = useCallback(candidate => {
        if (isDraggingRef.current) return

        setProgress(previous => {
            const next = retainMediaDockProgress(previous, candidate(previous))
            if (next === previous) return previous
            incrementMediaDockQaCounter('progress.statePublications')
            return next
        })
    }, [])

    useEffect(() => {
        let currentOwner = null
        let clearOwnerProgress = () => {}

        const publishOwnerProgress = owner => {
            publishProgress(previous =>
                owner
                    ? getMediaOwnerPlaybackSnapshot(owner, previous)
                    : readStoredProgress(mediaKey, previous),
            )
        }

        const bindCurrentOwner = () => {
            clearOwnerProgress()
            clearOwnerProgress = () => {}

            currentOwner = findMountedOwnerForMediaKey(mediaKey)
            publishOwnerProgress(currentOwner)

            if (!isDocumentVisible) return

            if (isNativeMediaOwner(currentOwner)) {
                const mediaEl = currentOwner.mediaEl
                const handleProgressEvent = () => publishOwnerProgress(currentOwner)

                mediaEl.addEventListener('timeupdate', handleProgressEvent)
                mediaEl.addEventListener('durationchange', handleProgressEvent)
                mediaEl.addEventListener('loadedmetadata', handleProgressEvent)
                mediaEl.addEventListener('seeking', handleProgressEvent)
                mediaEl.addEventListener('seeked', handleProgressEvent)
                clearOwnerProgress = () => {
                    mediaEl.removeEventListener('timeupdate', handleProgressEvent)
                    mediaEl.removeEventListener('durationchange', handleProgressEvent)
                    mediaEl.removeEventListener('loadedmetadata', handleProgressEvent)
                    mediaEl.removeEventListener('seeking', handleProgressEvent)
                    mediaEl.removeEventListener('seeked', handleProgressEvent)
                }
                return
            }

            if (!isPlaying) return

            const intervalMs = isExpanded
                ? CONTROLLER_PROGRESS_INTERVAL_MS
                : COLLAPSED_CONTROLLER_PROGRESS_INTERVAL_MS
            const interval = window.setInterval(() => {
                const latestOwner = findMountedOwnerForMediaKey(mediaKey)
                if (latestOwner?.ownerId !== currentOwner?.ownerId) {
                    bindCurrentOwner()
                    return
                }
                publishOwnerProgress(latestOwner)
            }, intervalMs)
            clearOwnerProgress = () => window.clearInterval(interval)
        }

        const unsubscribePlaybackState = subscribeToMediaPlaybackState(mediaKey, nextState => {
            if (isNativeMediaOwner(currentOwner)) return
            publishProgress(previous => ({
                currentTime: Number.isFinite(nextState?.currentTime)
                    ? nextState.currentTime
                    : previous.currentTime,
                duration:
                    Number.isFinite(nextState?.duration) && nextState.duration > 0
                        ? nextState.duration
                        : previous.duration,
            }))
        })
        const unsubscribeDock = subscribeToMediaDock(event => {
            if (!OWNER_CHANGE_EVENTS.has(event?.type)) return
            if (event?.mediaKey && event.mediaKey !== mediaKey) return
            bindCurrentOwner()
        })

        bindCurrentOwner()
        return () => {
            clearOwnerProgress()
            unsubscribePlaybackState()
            unsubscribeDock()
        }
    }, [isDocumentVisible, isExpanded, isPlaying, mediaKey, publishProgress])

    const handleSeek = useCallback(
        event => {
            const nextTime = Number(event.currentTarget.value)
            if (!Number.isFinite(nextTime)) return

            incrementMediaDockQaCounter('progress.statePublications')
            setProgress(previous => ({ ...previous, currentTime: nextTime }))
            seekMediaKey(mediaKey, nextTime)
        },
        [mediaKey],
    )

    const startDragging = useCallback(
        event => {
            isDraggingRef.current = true
            event.currentTarget.setPointerCapture?.(event.pointerId)
            onDraggingChange?.(true)
        },
        [onDraggingChange],
    )
    const stopDragging = useCallback(() => {
        if (!isDraggingRef.current) return
        isDraggingRef.current = false
        onDraggingChange?.(false)
    }, [onDraggingChange])

    const progressPercent =
        progress.duration > 0 ? Math.min((progress.currentTime / progress.duration) * 100, 100) : 0

    if (variant === 'hairline') {
        return (
            <span
                className={cn(styles.progressHairline, styles.progressHairlineVideo)}
                style={{ width: `${progressPercent}%` }}
                aria-hidden="true"
            />
        )
    }

    const seekInput = (
        <input
            type="range"
            min="0"
            max={progress.duration > 0 ? progress.duration : 0}
            step="0.01"
            value={Math.min(
                progress.currentTime,
                progress.duration > 0 ? progress.duration : progress.currentTime,
            )}
            className={styles.seekSlider}
            style={buildDockProgressStyle(progressPercent)}
            disabled={!(progress.duration > 0) || liveRadio}
            onChange={handleSeek}
            onInput={handleSeek}
            // On the stage tile the surface underneath is the thread link.
            onClick={stopEvent}
            onPointerDown={startDragging}
            onPointerUp={stopDragging}
            onPointerCancel={stopDragging}
            onBlur={stopDragging}
            aria-label="Seek"
            title={liveRadio ? 'Live on Treechat Music' : undefined}
        />
    )

    if (variant === 'stacked') {
        return (
            <>
                <div className={audioStyles.audioScrimSeekRow}>{seekInput}</div>
                <div className={audioStyles.audioScrimControlsRow}>
                    <span className={cn(styles.timeText, audioStyles.audioTimeText)}>
                        {formatDockTime(progress.currentTime)}
                        {' / '}
                        {formatDockTime(progress.duration)}
                    </span>
                    {children}
                </div>
            </>
        )
    }

    return (
        <>
            <span className={styles.timeText}>{formatDockTime(progress.currentTime)}</span>
            {seekInput}
            <span className={styles.timeText}>{formatDockTime(progress.duration)}</span>
        </>
    )
}

export default React.memo(MediaDockProgress)
